"use client";

import { FC } from "react";
import cx from "classnames";
import styles from "./header-additional.module.scss";
import DropdownSecondary, { IDropdownSecondaryOption } from "./dropdown-secondary";
import { formatTimePeriod } from "../../../utils/stringFormatter";
import { getLanguage } from "../../../utils/localeUtils";
import { DealerDepartmentBaseInfo, IDealerInfo, LogoPosition } from "../../../types/site";
import SocialMediaItem from "../../common-components/social-media/social-media";
import { CustomPhonesData, RequestContext } from "../../../types/ui";
import NotIsDevice from "../../common-components/is-device/not-is-device";
import { DeviceType } from "../../common-components/is-device/device-utils";
import HeaderPhones from "./header-phones";
import { getLocaleText, Translations } from "../../../utils/translations";
import { LeadSection } from "@/types/lead";

interface IHeaderAdditional {
  hidden?: boolean;
  context: RequestContext;
  logoUrl: string;
  logoPosition: LogoPosition;
  dealer: IDealerInfo;
  customPhonesData: CustomPhonesData;
  translations: Translations;
}

const HeaderAdditional: FC<IHeaderAdditional> = ({
  hidden,
  context,
  logoUrl,
  logoPosition,
  dealer,
  customPhonesData,
  translations,
}) => {
  const socialMedias = dealer?.socialMedias || [];
  const closedText = getLocaleText(translations, "footer-header", "closed");

  const language = getLanguage(context.locale);
  const primaryDepartment = dealer?.departments.find((d) => d.primary === true);

  if (!primaryDepartment) {
    return null;
  }

  const dealerSchedule = primaryDepartment.schedule;
  const scheduleItems: IDropdownSecondaryOption[] = dealerSchedule.map((item, index) => ({
    value: index,
    text1: new Date(2022, 2, 20 + item.dayOfWeek).toLocaleDateString(language, { weekday: "short" }),
    text2: item.workingTime ? formatTimePeriod(item.workingTime!) : closedText,
  }));

  const sunday = scheduleItems.pop();
  scheduleItems.unshift(sunday!);

  const curDate = new Date();
  const dayOfWeek = curDate.getDay();
  if (scheduleItems.length > dayOfWeek) {
    scheduleItems[dayOfWeek].active = true;
  }

  const departmentsBaseInfos: DealerDepartmentBaseInfo[] = dealer.departments
    .filter(({ title, phone }) => {
      return Object.values(LeadSection).includes(title) && !!phone;
    })
    .map((department) => ({
      title: department.title,
      phone: department.phone,
      primary: department.primary,
    }));

  return (
    <div
      className={cx(styles.additionalInfo, {
        [styles.hidden]: hidden,
        [styles.centerMenu]: logoPosition === LogoPosition.CENTER,
      })}
    >
      <NotIsDevice device={DeviceType.tablet} className={styles.social}>
        {socialMedias.map((item) => (
          <SocialMediaItem media={item} className={styles.socialLink} key={item?.type} />
        ))}
      </NotIsDevice>
      {logoPosition === LogoPosition.CENTER && (
        <NotIsDevice device={DeviceType.tablet}>
          <a href={"/"} title={"logo"} className={styles.logo}>
            {logoUrl && <img src={logoUrl} alt="logo" />}
          </a>
        </NotIsDevice>
      )}
      <DropdownSecondary
        options={scheduleItems}
        className={styles.days}
        disabledChange
        centerMenu={logoPosition === LogoPosition.CENTER}
      />
      <HeaderPhones
        customPhonesData={customPhonesData}
        departments={departmentsBaseInfos}
        logoPosition={logoPosition}
      />
    </div>
  );
};

export default HeaderAdditional;
